<template><div><dt-notice
  kind="warning"
  :hideClose="true"
  class="d-wmx100p"
>
  <template #default>
    <p class="d-body--md-compact">Before applying a typography utility, first consider using <dt-link to="/design/typography/" kind="muted" class="d-fw-semibold">Dialtone's text styles</dt-link> that bundles Font family, Font weight, Font size, and Line height together.</p>
  </template>
</dt-notice>
<h2 id="sans-serif" tabindex="-1" class="d-docsite--header-2">Sans-Serif <a class="header-anchor d-link d-docsite--link d-link" href="#sans-serif" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-ff-sans</code> to apply a Sans-Serif font stack.</p>
<code-well-header>
  <p class="d-ff-sans">The quick brown fox jumps over the lazy dog.</p>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-ff-sans<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="mono" tabindex="-1" class="d-docsite--header-2">Mono <a class="header-anchor d-link d-docsite--link d-link" href="#mono" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-ff-mono</code> to apply a Monospace font stack.</p>
<code-well-header>
  <p class="d-ff-mono">The quick brown fox jumps over the lazy dog.</p>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-ff-mono<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="marketing" tabindex="-1" class="d-docsite--header-2">Marketing <a class="header-anchor d-link d-docsite--link d-link" href="#marketing" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dialtone supports select marketing fonts and weights. Use the following combinations to apply the marketing font stack.</p>
<code-well-header>
  <p class="d-ff-marketing">The quick brown fox jumps over the lazy dog.</p>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-ff-marketing<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="css-variables" tabindex="-1" class="d-docsite--header-2">CSS Variables <a class="header-anchor d-link d-docsite--link d-link" href="#css-variables" aria-hidden="true">#</a></h2>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-t0">
        <tr>
          <th scope="col" class="d-p0 d-bbw0 d-w40p"><div class="d-p16 d-bb d-bc-default d-bbw1">Variable</div></th>
          <th scope="col" class="d-p0 d-bbw0"><div class="d-p16 d-bb d-bc-default d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="{ var: varName, output } in fontFamily.slice(0, -1)">
          <td class="d-code--sm d-docsite-code">var(--ff-{{ varName }})</td>
          <td class="d-code--sm">{{ output }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
<h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<clamped-table-wrapper>
  <div>
    <table class="d-table dialtone-doc-table">
      <thead class="d-bgc-primary d-ps-sticky d-zi-base1 d-t0">
        <tr>
          <th scope="col" class="d-p0 d-bbw0 d-w40p"><div class="d-p16 d-bb d-bc-default d-bbw1">Class</div></th>
          <th scope="col" class="d-p0 d-bbw0"><div class="d-p16 d-bb d-bc-default d-bbw1">Output</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="{ var: varName, output } in fontFamily">
          <td class="d-code--sm d-docsite-code">.d-ff-{{ varName }}</td>
          <td class="d-code--sm">font-family: {{ output }} !important;</td>
        </tr>
      </tbody>
    </table>
  </div>
</clamped-table-wrapper>
</div></template>


<script setup>
  import { fontFamily } from '@data/type.json';
  import ClampedTableWrapper from '@baseComponents/ClampedTableWrapper.vue';
</script>